/**
 * liquiditySummary (#1315) — derive the farmer-facing header figures from the
 * daily series so the surface reads as plain language, not a raw chart:
 *   • current balance (the value at today),
 *   • the lowest forecast value + the first day it breaches the KK-limit,
 *   • the overrun (how far that low point is past the limit),
 *   • the Ist→Prognose boundary dates.
 * Pure (no React); recomputed per render like the rest of the surface.
 */
import type { BalanceSeriesResult } from './types';
import { addDays } from './computeBalanceSeries';

export interface LiquiditySummary {
  /** Balance as of ``today`` (the "what do I have right now" number). */
  currentBalanceEur: number | null;
  /** Lowest projected balance after the anchor, or null when nothing is projected. */
  lowestForecastEur: number | null;
  /** First post-anchor day the projection drops below the KK-limit (red only). */
  breachDate: string | null;
  /** How far the lowest point sits past the limit (>0), or null when it never does. */
  overrunEur: number | null;
  /** Last day of actual bank data (= the derived Ist horizon). */
  istStandDate: string;
  /** First projected day (= Ist horizon + 1). */
  prognoseStartDate: string;
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

export function liquiditySummary(
  series: BalanceSeriesResult,
  horizonDate: string,
  today: string,
): LiquiditySummary {
  const ds = series.dailySeries;
  const kkLimit = series.verdict.kkLimitEur;

  // Current balance = the series value AT today; fall back to the last point
  // on/before today (the series is date-sorted), then null for an empty series.
  let currentBalanceEur: number | null = null;
  for (const p of ds) {
    if (p.date > today) break;
    currentBalanceEur = p.balanceEur;
  }

  // Forecast region = strictly after the horizon RISK is judged from. Post #1639
  // that is the derived horizon (latest actual), not the static anchor, so the
  // newly-actual window past the anchor is excluded from the breach/overrun.
  //
  // #3241: on the "Alle Konten" aggregate the RENDERED horizon (``horizonDate``,
  // which still drives the caption dates below) is widened past a dormant
  // account's own horizon, while the verdict keeps the conservative one. Scanning
  // from the widened boundary would skip that account's forecast dip and hand
  // ``riskSentence`` a later, wrong "Engpass ab …" date with a smaller overrun.
  // Take the EARLIER of the two so this stays pinned to the verdict's projection,
  // whichever boundary the caller passed. The min is defence, not logic:
  // ``verdictHorizon <= istHorizon`` holds structurally (a MIN over a subset is
  // >= the MIN over the whole set) and the page always passes ``istHorizon``, so
  // in practice this always selects ``verdictHorizon``.
  const forecastFrom =
    series.verdictHorizon < horizonDate ? series.verdictHorizon : horizonDate;
  let lowestForecastEur: number | null = null;
  let breachDate: string | null = null;
  for (const p of ds) {
    if (p.date <= forecastFrom) continue;
    if (lowestForecastEur == null || p.balanceEur < lowestForecastEur) {
      lowestForecastEur = p.balanceEur;
    }
    if (breachDate == null && kkLimit != null && p.balanceEur < kkLimit) {
      breachDate = p.date;
    }
  }

  const overrunEur =
    kkLimit != null && lowestForecastEur != null && lowestForecastEur < kkLimit
      ? round2(kkLimit - lowestForecastEur)
      : null;

  return {
    currentBalanceEur,
    lowestForecastEur,
    breachDate,
    overrunEur,
    istStandDate: horizonDate,
    prognoseStartDate: horizonDate ? addDays(horizonDate, 1) : '',
  };
}
